import React, { useEffect, useState } from 'react';
import '../../../../assets/styles/add-contents.scss';
import {
    getQuizForContent,
    upsertQuizForContent,
    deleteQuizForContent,
    importQuizzesForContent,
    type QuizLanguageCode,
    type QuizQuestionInput,
    type QuizDTO,
} from '../../../../api/quiz';

// Rozszerzamy zestaw języków o IT i FR (frontendowo)
type ApiLang = QuizLanguageCode;          // 'PL' | 'EN' | 'ES' | 'DE' | 'UK' | 'IT' | 'FR'
type Lang = ApiLang;

const ALLOWED: Lang[] = ['PL', 'EN', 'ES', 'DE', 'UK', 'IT', 'FR'];

function ensureLang(v: string | undefined | null): Lang {
    const U = String(v || '').toUpperCase();
    return (ALLOWED as string[]).includes(U) ? (U as Lang) : 'PL';
}

/* ====== Proste dialogi (stylistycznie zgodne z add-contents.scss) ====== */

function InfoDialog({
    open,
    title,
    message,
    onClose,
    variant = 'ok',
}: {
    open: boolean;
    title?: string;
    message?: string;
    variant?: 'ok' | 'error';
    onClose: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className={`modal-card ${variant === 'error' ? 'modal-error' : ''}`}>
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-primary" onClick={onClose}>
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}

function ConfirmDialog({
    open,
    title = 'Potwierdź',
    message,
    confirmLabel = 'Tak',
    cancelLabel = 'Anuluj',
    danger = false,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onCancel}>
                        {cancelLabel}
                    </button>
                    <button className={danger ? 'delete-btn' : 'btn-primary'} onClick={onConfirm}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ====== Typy propsów ====== */

interface AddQuizProps {
    /** ID treści z backendu – bez tego nie robimy nic */
    contentId: number | null;
    /** Domyślny język quizu (np. język oryginału) */
    baseLanguage?: Lang;
    /** Gdy true – pola tylko do odczytu (kłódka z AddContents) */
    locked?: boolean;
    /**
     * Czy treść ma zapisany tekst (żeby odblokować quiz).
     * Możesz policzyć jako !!form.text_content, albo bardziej restrykcyjnie – jeśli wiesz, że jest już w DB.
     */
    hasText?: boolean;
}

/* ====== Komponent główny ====== */

const AddQuiz: React.FC<AddQuizProps> = ({
    contentId,
    baseLanguage = 'PL',
    locked = false,
    hasText = true,
}) => {
    const [selectedLang, setSelectedLang] = useState<Lang>(ensureLang(baseLanguage));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);

    const [quiz, setQuiz] = useState<QuizDTO | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);

    const [questions, setQuestions] = useState<QuizQuestionInput[]>([]);

    const [infoOpen, setInfoOpen] = useState(false);
    const [infoTitle, setInfoTitle] = useState<string | undefined>();
    const [infoMsg, setInfoMsg] = useState<string | undefined>();
    const [infoVariant, setInfoVariant] = useState<'ok' | 'error'>('ok');

    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        message?: string;
        danger?: boolean;
        onConfirm?: () => void;
    }>({ open: false });

    const showOk = (msg: string, title = 'Sukces') => {
        setInfoVariant('ok');
        setInfoTitle(title);
        setInfoMsg(msg);
        setInfoOpen(true);
    };

    const showErr = (msg: string, title = 'Błąd') => {
        setInfoVariant('error');
        setInfoTitle(title);
        setInfoMsg(msg);
        setInfoOpen(true);
    };

    const hydrateFromDTO = (data: QuizDTO | null) => {
        if (!data) {
            setQuiz(null);
            setTitle('');
            setDescription('');
            setIsActive(true);
            setQuestions([]);
            return;
        }

        setQuiz(data);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setIsActive(data.is_active);

        const mapped: QuizQuestionInput[] = (data.questions || [])
            .map((q, index) => {
                const wrongs = q.wrong_answers || [];
                const wrongAnswers: [string, string, string] = [
                    wrongs[0] ?? '',
                    wrongs[1] ?? '',
                    wrongs[2] ?? '',
                ];
                return {
                    term: q.term,
                    correct_answer: q.correct_answer,
                    wrong_answers: wrongAnswers,
                    order_index: q.order_index ?? index,
                };
            })
            .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

        setQuestions(mapped);
    };

    // ====== Ładowanie quizu przy zmianie contentId / języka ======
    useEffect(() => {
        if (!contentId || !hasText) {
            setQuiz(null);
            setQuestions([]);
            setTitle('');
            setDescription('');
            setIsActive(true);
            return;
        }

        let cancelled = false;

        const fetchQuiz = async () => {
            setLoading(true);
            try {
                const data: QuizDTO | null = await getQuizForContent(
                    contentId,
                    selectedLang as ApiLang
                );
                if (cancelled) return;

                hydrateFromDTO(data);
            } catch (e: any) {
                if (!cancelled) {
                    showErr(e?.error || 'Błąd podczas pobierania quizu.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchQuiz();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId, selectedLang, hasText]);

    // ====== Operacje na pytaniach ======

    const addQuestion = () => {
        setQuestions((prev) => [
            ...prev,
            {
                term: '',
                correct_answer: '',
                wrong_answers: ['', '', ''] as [string, string, string],
            },
        ]);
    };

    const updateQuestion = (index: number, patch: Partial<QuizQuestionInput>) => {
        setQuestions((prev) =>
            prev.map((q, i) => (i === index ? { ...q, ...patch } : q))
        );
    };

    const updateWrongAnswer = (
        qIndex: number,
        wrongIdx: 0 | 1 | 2,
        value: string,
    ) => {
        setQuestions((prev) =>
            prev.map((q, i) => {
                if (i !== qIndex) return q;
                const nextWrongs = [...q.wrong_answers] as [string, string, string];
                nextWrongs[wrongIdx] = value;
                return { ...q, wrong_answers: nextWrongs };
            }),
        );
    };

    const removeQuestion = (index: number) => {
        setQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    const moveQuestion = (index: number, direction: 'up' | 'down') => {
        setQuestions((prev) => {
            const next = [...prev];
            const target = direction === 'up' ? index - 1 : index + 1;
            if (target < 0 || target >= next.length) return prev;
            const tmp = next[index];
            next[index] = next[target];
            next[target] = tmp;
            return next;
        });
    };

    // ====== Walidacja i zapis ======

    const validate = (): string | null => {
        if (!questions.length) return 'Dodaj przynajmniej jedno pytanie.';

        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.term.trim()) return `Pytanie #${i + 1}: podaj słówko.`;
            if (!q.correct_answer.trim())
                return `Pytanie #${i + 1}: podaj poprawne tłumaczenie.`;

            const wrongs = q.wrong_answers.map((w) => w.trim());
            if (wrongs.some((w) => !w))
                return `Pytanie #${i + 1}: wszystkie 3 błędne odpowiedzi muszą być uzupełnione.`;

            const allOptions = [q.correct_answer.trim(), ...wrongs];
            const unique = new Set(allOptions.map((x) => x.toLowerCase()));
            if (unique.size < allOptions.length) {
                return `Pytanie #${i + 1}: odpowiedzi (poprawna + błędne) muszą się od siebie różnić.`;
            }
        }

        return null;
    };

    const handleSave = async () => {
        if (!contentId) return;
        const error = validate();
        if (error) {
            showErr(error);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                language_code: selectedLang as ApiLang, // rzutowanie do QuizLanguageCode
                title: title.trim() || `Quiz słówek (${selectedLang})`,
                description: description.trim() || null,
                is_active: isActive,
                questions: questions.map((q, idx) => ({
                    term: q.term.trim(),
                    correct_answer: q.correct_answer.trim(),
                    wrong_answers: [
                        q.wrong_answers[0].trim(),
                        q.wrong_answers[1].trim(),
                        q.wrong_answers[2].trim(),
                    ] as [string, string, string],
                    order_index: idx,
                })),
            };

            await upsertQuizForContent(contentId, payload);
            showOk('Quiz zapisany.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zapisie quizu.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        if (!contentId) return;
        setConfirmState({
            open: true,
            title: `Usunąć quiz dla języka ${selectedLang}?`,
            message: 'Operacji nie można cofnąć. Użytkownicy stracą dostęp do tego quizu.',
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteQuizForContent(contentId, selectedLang as ApiLang);
                    setQuiz(null);
                    setQuestions([]);
                    setTitle('');
                    setDescription('');
                    setIsActive(true);
                    showOk('Quiz usunięty.');
                } catch (e: any) {
                    showErr(e?.error || 'Błąd przy usuwaniu quizu.');
                } finally {
                    setConfirmState((s) => ({ ...s, open: false }));
                }
            },
        });
    };

    // ====== Import z pliku (wielojęzyczny) ======

    const handleImportFile = async (file: File) => {
        if (!contentId) return;
        setImporting(true);
        try {
            const text = await file.text();

            let json: any;
            try {
                json = JSON.parse(text);
            } catch {
                showErr('Plik musi być poprawnym JSON-em.');
                return;
            }

            if (!json || !Array.isArray(json.items) || json.items.length === 0) {
                showErr('Plik musi zawierać pole "items" z listą quizów.');
                return;
            }

            const result = await importQuizzesForContent(contentId, json);

            showOk(
                `Zaimportowano quizy dla ${result.imported_count} języków.\n` +
                `Odświeżam quiz dla języka ${selectedLang}.`
            );

            // po imporcie odśwież quiz dla aktualnie wybranego języka
            try {
                const data: QuizDTO | null = await getQuizForContent(
                    contentId,
                    selectedLang as ApiLang
                );
                hydrateFromDTO(data);
            } catch (e: any) {
                showErr(e?.error || 'Błąd podczas odświeżania quizu po imporcie.');
            }
        } catch (e: any) {
            console.error(e);
            showErr(e?.error || 'Błąd podczas importu quizów.');
        } finally {
            setImporting(false);
        }
    };

    // ====== Widoki stanu ======

    if (!contentId) {
        return (
            <div className="translations-card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>Quiz słówek</h3>
                <div className="add-info">
                    Zapisz treść (utwórz ją), aby dodać quiz.
                </div>
            </div>
        );
    }

    if (!hasText) {
        return (
            <div className="translations-card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>Quiz słówek</h3>
                <div className="add-info">
                    Dodaj i zapisz <strong>tekst treści</strong>, aby odblokować quiz.
                    Quiz jest opcjonalny, ale musi być powiązany z istniejącym tekstem.
                </div>
            </div>
        );
    }

    const readOnly = locked;

    return (
        <>
            <div className="translations-card" style={{ marginTop: 16 }}>
                <div className="tr-header">
                    <h3 style={{ margin: 0 }}>Quiz słówek</h3>
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <select
                            value={selectedLang}
                            onChange={(e) => setSelectedLang(ensureLang(e.target.value))}
                            disabled={loading}
                        >
                            {ALLOWED.map((l) => (
                                <option key={l} value={l}>
                                    {l}
                                </option>
                            ))}
                        </select>
                        {quiz && (
                            <span className="badge">
                                {(quiz.questions?.length ?? 0)} pytań •{' '}
                                {quiz.is_active ? 'Aktywny' : 'Nieaktywny'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Sekcja importu z pliku */}
                <div
                    style={{
                        marginTop: 8,
                        marginBottom: 12,
                        padding: 10,
                        borderRadius: 6,
                        border: '1px dashed #c7c9d3',
                        background: '#fafbff',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                    }}
                >
                    <strong>Import quizów z pliku</strong>
                    <span className="add-info">
                        Możesz zaimportować quizy dla wielu języków naraz z pliku JSON
                        (format: <code>{'{ items: QuizInput[] }'}</code>).
                    </span>
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <input
                            type="file"
                            accept=".json,application/json,text/plain"
                            disabled={readOnly || importing}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    void handleImportFile(file);
                                    e.target.value = '';
                                }
                            }}
                        />
                        {importing && <span className="badge">Importuję…</span>}
                    </div>
                </div>

                {loading ? (
                    <div className="add-info">Ładuję quiz…</div>
                ) : (
                    <>
                        <p className="add-info" style={{ marginTop: 8 }}>
                            Quiz jest opcjonalny. Użytkownik w aplikacji mobilnej będzie mógł
                            rozwiązać go tylko jeden raz, a statystyki i porównanie z innymi
                            liczone są po stronie backendu.
                        </p>

                        <div className="translation-form" style={{ marginTop: 12 }}>
                            <label>
                                Tytuł quizu
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={`Np. Quiz słówek (${selectedLang})`}
                                    disabled={readOnly}
                                />
                            </label>

                            <label>
                                Opis (opcjonalnie)
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Krótki opis quizu, np. które słówka obejmuje."
                                    disabled={readOnly}
                                />
                            </label>

                            <label className={`violet-toggle ${readOnly ? 'is-disabled' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    disabled={readOnly}
                                />
                                <span className="vt-track" aria-hidden="true">
                                    <span className="vt-thumb" />
                                </span>
                                <span className="vt-text">
                                    Quiz aktywny{' '}
                                    <span className="vt-state">{isActive ? 'TAK' : 'NIE'}</span>
                                </span>
                            </label>
                        </div>

                        {/* Lista pytań */}
                        <div style={{ marginTop: 16 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8,
                                    gap: 8,
                                }}
                            >
                                <h4 style={{ margin: 0 }}>Pytania ({questions.length})</h4>
                                <button
                                    type="button"
                                    className="btn-link-add"
                                    onClick={addQuestion}
                                    disabled={readOnly}
                                >
                                    + Dodaj pytanie
                                </button>
                            </div>

                            {questions.length === 0 ? (
                                <div className="add-info">
                                    Brak pytań w tym quizie. Dodaj pierwsze pytanie, aby utworzyć
                                    quiz dla języka <strong>{selectedLang}</strong>.
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 10,
                                    }}
                                >
                                    {questions.map((q, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                border: '1px solid #eef0f4',
                                                borderRadius: 5,
                                                padding: 10,
                                                background: '#ffffff',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: 8,
                                                    gap: 8,
                                                }}
                                            >
                                                <span className="badge-muted">
                                                    Pytanie #{idx + 1}
                                                </span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button
                                                        type="button"
                                                        className="btn-ghost"
                                                        onClick={() => moveQuestion(idx, 'up')}
                                                        disabled={readOnly || idx === 0}
                                                        title="Przenieś w górę"
                                                    >
                                                        ▲
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-ghost"
                                                        onClick={() => moveQuestion(idx, 'down')}
                                                        disabled={
                                                            readOnly ||
                                                            idx === questions.length - 1
                                                        }
                                                        title="Przenieś w dół"
                                                    >
                                                        ▼
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="delete-btn"
                                                        onClick={() => removeQuestion(idx)}
                                                        disabled={readOnly}
                                                    >
                                                        Usuń
                                                    </button>
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gap: 8,
                                                    gridTemplateColumns: '1fr',
                                                }}
                                            >
                                                <label>
                                                    <span>Słówko (z tekstu)</span>
                                                    <input
                                                        type="text"
                                                        value={q.term}
                                                        onChange={(e) =>
                                                            updateQuestion(idx, {
                                                                term: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Np. apple"
                                                        disabled={readOnly}
                                                    />
                                                </label>

                                                <label>
                                                    <span>Poprawne tłumaczenie</span>
                                                    <input
                                                        type="text"
                                                        value={q.correct_answer}
                                                        onChange={(e) =>
                                                            updateQuestion(idx, {
                                                                correct_answer: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Np. jabłko"
                                                        disabled={readOnly}
                                                    />
                                                </label>

                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gap: 6,
                                                        gridTemplateColumns:
                                                            'repeat(auto-fit, minmax(180px, 1fr))',
                                                    }}
                                                >
                                                    <label>
                                                        <span>Błędna odpowiedź #1</span>
                                                        <input
                                                            type="text"
                                                            value={q.wrong_answers[0] || ''}
                                                            onChange={(e) =>
                                                                updateWrongAnswer(
                                                                    idx,
                                                                    0,
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                    </label>
                                                    <label>
                                                        <span>Błędna odpowiedź #2</span>
                                                        <input
                                                            type="text"
                                                            value={q.wrong_answers[1] || ''}
                                                            onChange={(e) =>
                                                                updateWrongAnswer(
                                                                    idx,
                                                                    1,
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                    </label>
                                                    <label>
                                                        <span>Błędna odpowiedź #3</span>
                                                        <input
                                                            type="text"
                                                            value={q.wrong_answers[2] || ''}
                                                            onChange={(e) =>
                                                                updateWrongAnswer(
                                                                    idx,
                                                                    2,
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Przyciski zapisu / usuwania */}
                        <div
                            className="form-buttons"
                            style={{
                                marginTop: 16,
                                justifyContent: 'flex-start',
                                gap: 10,
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={readOnly || saving}
                            >
                                {saving ? 'Zapisuję...' : 'Zapisz quiz'}
                            </button>

                            {quiz && (
                                <button
                                    type="button"
                                    className="delete-btn"
                                    onClick={handleDelete}
                                    disabled={readOnly}
                                >
                                    Usuń quiz
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Dialogi */}
            <InfoDialog
                open={infoOpen}
                title={infoTitle}
                message={infoMsg}
                variant={infoVariant}
                onClose={() => setInfoOpen(false)}
            />
            <ConfirmDialog
                open={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={
                    confirmState.onConfirm ||
                    (() => setConfirmState((s) => ({ ...s, open: false })))
                }
                onCancel={() => setConfirmState((s) => ({ ...s, open: false }))}
            />
        </>
    );
};

export default AddQuiz;
